import create from 'zustand';
import { useZendesk } from './Zendesk';
import { useFrame, useTranslation } from '~/shared/utils';
import { useDixa } from './Dixa';
import { useEffect, useMemo, useState } from 'react';
import { useCookieConsent, useNotification } from '~/shared/hooks';
import { useCookieInformation } from '~/shared/components';

export type ChatState = 'pristine' | 'loading' | 'open' | 'closed' | 'error';

export type ChatStore = {
    chatState: ChatState;
    chatError?: Error;
    setChatState: (state: ChatState) => void;
    setChatError: (error: Error) => void;
};

/**
 * Manage chat state
 */
const useChatState = create<ChatStore>((set) => ({
    chatState: 'pristine',
    setChatState: (state: ChatState) => set({ chatState: state }),
    setChatError: (error: Error) => set({ chatError: error, chatState: 'error' }),
}));

export const useChat = () => {
    const { translate } = useTranslation();
    const [shouldLoadChat, setShouldLoadChat] = useState(false);
    const { chatState, setChatState, chatError, setChatError } = useChatState();
    const { functional } = useCookieConsent();
    const { renewConsent } = useCookieInformation();
    const { push, dismiss } = useNotification();
    const { showWindow, hideButton } = useZendesk();
    const { showDixa } = useDixa();
    const { data: frame } = useFrame();
    const chatProvider = frame?.settings?.customerService?.chatProvider;
    const hideChatButton = useMemo(() => (chatProvider === 'zendesk' ? hideButton : () => null), [
        chatProvider,
        hideButton,
    ]);

    const showChat = () => {
        setShouldLoadChat(true);
        if (functional) {
            chatProvider === 'zendesk' ? showWindow() : showDixa();
            return;
        }
        push({
            severity: 'info',
            duration: 8000,
            headline: translate('global.consent.chat.error.headline'),
            text: translate('global.consent.chat.error.text'),
            onClick: renewConsent,
        });
    };

    // Close all notifications, if functional cookies are accepted and chat should load
    useEffect(() => {
        if (chatState === 'error') {
            return;
        }
        if (functional && shouldLoadChat) {
            dismiss();
            if (chatState !== 'pristine') {
                return;
            }
            setChatState('loading');
        }
    }, [functional, dismiss, shouldLoadChat, setChatState, chatState]);

    // Handle if chatState is 'error'
    useEffect(() => {
        if (chatState !== 'error') {
            return;
        }
        setShouldLoadChat(false);
        setChatState('closed');
        hideChatButton();
        push({});
        console.error(chatError);
    }, [chatState, chatError, setChatState, push, setShouldLoadChat, hideChatButton]);

    const chatEnabled = Boolean(chatProvider);

    return { setChatState, setChatError, showChat, hideChatButton, chatState, chatEnabled };
};
